/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

// Insert any custom theme js here...

/*-----------------------------------------------------------------------------/
/ Bundle Discount Reconciliation
/-----------------------------------------------------------------------------*/

let domHasLoaded = false;

if (!domHasLoaded) {
  (function () {
    domHasLoaded = true;

    // -----------------------------------------
    // Guards to prevent loops / rapid re-entry
    // -----------------------------------------
    let isProcessingBundleUpdate = false;
    let lastUpdateTimestamp = 0;
    const COOLDOWN_MS = 2000; // 2s cool-down between cart writes

    // -----------------------------------------
    // Bundle configuration
    // -----------------------------------------
    // Use product_ids for grouping
    const GROUP_A_PRODUCTS = [8709314052346, 7538857443578];
    const GROUP_B_PRODUCTS = [8709404328186, 8015101657338];

    // Line-item properties used to tag discounted pairs
    const BUNDLE_ID = '7b3fd806-c7aa-40bc-85f2-146694592e84';
    const DISCOUNT_PROPS = (idx) => ({
      _dynamicBoxIndex: idx,
      _dynamicBoxId: BUNDLE_ID
    });

    // -----------------------------------------
    // Shopify AJAX helpers
    // -----------------------------------------

    // Always include quantity; target by line "key" (stable id)
    async function changeLine({ id, quantity, properties }) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id, quantity, properties })
      });
      if (!res.ok) {
        let msg = `change.js failed ${res.status}`;
        try { msg += `: ${await res.text()}`; } catch(_) {}
        throw new Error(msg);
      }
      return res.json();
    }

    // Safest format: { items: [ { id, quantity, properties?, selling_plan? } ] }
    async function addLine({ variant_id, quantity, properties, selling_plan }) {
      if (!variant_id) throw new Error('addLine() missing variant_id');

      const item = { id: variant_id, quantity };
      if (properties && Object.keys(properties).length) item.properties = properties;
      if (selling_plan) item.selling_plan = selling_plan;

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [item] })
      });

      if (!res.ok) {
        let msg = `add.js failed ${res.status}`;
        try { msg += `: ${await res.text()}`; } catch(_) {}
        throw new Error(msg);
      }
      return res.json();
    }

    // Try to add as one-time. If Shopify says a selling plan is required, retry with provided plan.
    async function addNonDiscountedRespectingPlan(ref, qty) {
      const baseVariantId = ref?.variant_id;
      const basePlanId = ref?.selling_plan;

      // Attempt one-time (no selling_plan)
      try {
        return await addLine({ variant_id: baseVariantId, quantity: qty });
      } catch (e) {
        const msg = String(e.message || '');
        const needsPlan = /selling[_-]?plan/i.test(msg) || /requires.*subscription/i.test(msg);
        if (!needsPlan) throw e;

        // Retry with the plan if available (sub-only variant)
        if (!basePlanId) throw e;
        return await addLine({ variant_id: baseVariantId, quantity: qty, selling_plan: basePlanId });
      }
    }

    // -----------------------------------------
    // Utilities
    // -----------------------------------------

    // Robust variant/selling plan resolution (handles different payload shapes)
    function resolveVariantId(item) {
      return item?.variantId ?? item?.variant_id ?? item?.id ?? item?.variant?.id ?? null;
    }

    function resolveSellingPlanId(item) {
      return (
        item?.sellingPlanId ??
        item?.selling_plan ??
        item?.selling_plan_id ??
        item?.selling_plan_allocation?.selling_plan?.id ??
        item?.sellingPlan?.id ??
        null
      );
    }

    function mergedProps(currentProps, idxOrNull) {
      const props = { ...(currentProps || {}) };
      delete props._dynamicBoxIndex;
      delete props._dynamicBoxId;
      if (idxOrNull != null) {
        const tagged = DISCOUNT_PROPS(idxOrNull);
        props._dynamicBoxIndex = tagged._dynamicBoxIndex;
        props._dynamicBoxId = tagged._dynamicBoxId;
      }
      return props;
    }

    // -----------------------------------------
    // Core reconciliation
    // -----------------------------------------

    async function handleBundleDiscount(cart) {
      const timeSinceLastUpdate = Date.now() - lastUpdateTimestamp;
      if (isProcessingBundleUpdate || timeSinceLastUpdate < COOLDOWN_MS) {
        console.log('[bundle] Skipping—processing/cooldown');
        return;
      }
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
        console.log('[bundle] Empty/invalid cart');
        return;
      }

      isProcessingBundleUpdate = true;
      console.log('[bundle] Start reconciliation');

      try {
        // Normalize cart items (keep stable key, quantity, product_id, variant_id)
        const cartItems = cart.items.map((item, idx) => ({
          ...item,
          cartIndex: idx + 1,                                   // for logging
          productId: item.product_id ?? item.productId ?? item.product?.id,
          lineKey: item.key,                                    // stable for change.js
          variantId: item.variant_id ?? item.id ?? item.variantId ?? item.variant?.id,
          sellingPlanId:
            item.selling_plan ??
            item.selling_plan_id ??
            item.selling_plan_allocation?.selling_plan?.id ??
            item.sellingPlanId ??
            item.sellingPlan?.id,
          qty: item.quantity
        }));

        // Expand by quantity to count pairing capacity
        const groupAUnits = [];
        const groupBUnits = [];
        const otherLines = [];

        for (const it of cartItems) {
          if (GROUP_A_PRODUCTS.includes(it.productId)) {
            for (let i = 0; i < it.qty; i++) groupAUnits.push({ lineKey: it.lineKey, variantId: it.variantId });
          } else if (GROUP_B_PRODUCTS.includes(it.productId)) {
            for (let i = 0; i < it.qty; i++) groupBUnits.push({ lineKey: it.lineKey, variantId: it.variantId });
          } else {
            otherLines.push(it);
          }
        }

        const totalPairs = Math.min(groupAUnits.length, groupBUnits.length);

        // Allocate desired discounted qty per side (A and B) — independent maps
        const desiredDiscountQtyA = new Map(); // lineKey -> discounted units for Group A
        const desiredDiscountQtyB = new Map(); // lineKey -> discounted units for Group B

        function allocatePerSide(units, map) {
          let pairsLeft = totalPairs;
          for (const u of units) {
            if (pairsLeft <= 0) break;
            map.set(u.lineKey, (map.get(u.lineKey) || 0) + 1);
            pairsLeft -= 1;
          }
        }

        allocatePerSide(groupAUnits, desiredDiscountQtyA);
        allocatePerSide(groupBUnits, desiredDiscountQtyB);

        const changes = [];   // /cart/change.js requests
        const splitAdds = []; // deferred /cart/add.js for remainders
        let needAnyChange = false;

        // Reconcile Group A lines
        for (const line of cartItems.filter(ci => GROUP_A_PRODUCTS.includes(ci.productId))) {
          const currentProps = line.properties || {};
          const isDiscounted = currentProps._dynamicBoxId === BUNDLE_ID;
          const targetDiscountQty = Math.min(desiredDiscountQtyA.get(line.lineKey) || 0, line.qty);

          if (targetDiscountQty === 0) {
            if (isDiscounted) {
              changes.push({ id: line.lineKey, quantity: line.qty, properties: mergedProps(currentProps, null) });
              needAnyChange = true;
            }
          } else if (targetDiscountQty === line.qty) {
            if (!isDiscounted || currentProps._dynamicBoxIndex !== 1) {
              changes.push({ id: line.lineKey, quantity: line.qty, properties: mergedProps(currentProps, 1) });
              needAnyChange = true;
            }
          } else {
            const remainder = line.qty - targetDiscountQty;
            // shrink this line to discounted qty and keep discount props
            changes.push({ id: line.lineKey, quantity: targetDiscountQty, properties: mergedProps(currentProps, 1) });

            // queue add for the remainder as non-discounted (resolve ids now)
            const vId = resolveVariantId(line);
            const spId = resolveSellingPlanId(line);
            if (!vId) {
              console.warn('[bundle] No variant id for remainder add (Group A); skipping.', line);
            } else {
              splitAdds.push({ variant_id: vId, qty: remainder, selling_plan: spId });
            }
            needAnyChange = true;
          }
        }

        // Reconcile Group B lines
        for (const line of cartItems.filter(ci => GROUP_B_PRODUCTS.includes(ci.productId))) {
          const currentProps = line.properties || {};
          const isDiscounted = currentProps._dynamicBoxId === BUNDLE_ID;
          const targetDiscountQty = Math.min(desiredDiscountQtyB.get(line.lineKey) || 0, line.qty);

          if (targetDiscountQty === 0) {
            if (isDiscounted) {
              changes.push({ id: line.lineKey, quantity: line.qty, properties: mergedProps(currentProps, null) });
              needAnyChange = true;
            }
          } else if (targetDiscountQty === line.qty) {
            if (!isDiscounted || currentProps._dynamicBoxIndex !== 1) {
              changes.push({ id: line.lineKey, quantity: line.qty, properties: mergedProps(currentProps, 1) });
              needAnyChange = true;
            }
          } else {
            const remainder = line.qty - targetDiscountQty;
            changes.push({ id: line.lineKey, quantity: targetDiscountQty, properties: mergedProps(currentProps, 1) });

            const vId = resolveVariantId(line);
            const spId = resolveSellingPlanId(line);
            if (!vId) {
              console.warn('[bundle] No variant id for remainder add (Group B); skipping.', line);
            } else {
              splitAdds.push({ variant_id: vId, qty: remainder, selling_plan: spId });
            }
            needAnyChange = true;
          }
        }

        // Clean “other” lines that accidentally carry discount props
        for (const line of otherLines) {
          const currentProps = line.properties || {};
          if (currentProps._dynamicBoxId === BUNDLE_ID) {
            changes.push({ id: line.lineKey, quantity: line.qty, properties: mergedProps(currentProps, null) });
            needAnyChange = true;
          }
        }

        if (!needAnyChange) {
          console.log('[bundle] No updates needed');
          isProcessingBundleUpdate = false;
          return;
        }

        // Apply changes first to avoid merges surprising adds
        for (const c of changes) await changeLine(c);

        // Then execute adds (respect selling plan only if required)
        for (const { variant_id, qty, selling_plan } of splitAdds) {
          await addNonDiscountedRespectingPlan({ variant_id, selling_plan }, qty);
        }

        lastUpdateTimestamp = Date.now();
        console.log('[bundle] Reconciled bundle discounts');

        if (window.upcart && window.upcartRefreshCart) window.upcartRefreshCart();
      } catch (err) {
        console.error('[bundle] Error:', err);
      } finally {
        isProcessingBundleUpdate = false;
      }
    }

    // -----------------------------------------
    // Init: subscribe to UpCart updates
    // -----------------------------------------
    function init() {
      if (typeof window.upcartSubscribeCartUpdated === 'function') {
        console.log('[bundle] Initializing bundle discount handler…');
        window.upcartSubscribeCartUpdated(({ cart }) => {
          try {
            handleBundleDiscount(cart);
          } catch (e) {
            console.error('[bundle] Handler error:', e);
          }
        });

        // Optional: reconcile immediately if cart is already present
        if (window.UPCART && window.UPCART.cart) {
          handleBundleDiscount(window.UPCART.cart);
        }
      } else {
        console.warn('[bundle] upcartSubscribeCartUpdated not available. Retrying in 1s…');
        setTimeout(init, 1000);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();
}


/*-----------------------------------------------------------------------------/
/ Bundle Cart Properties Transform
/-----------------------------------------------------------------------------*/
document.addEventListener('DOMContentLoaded', function() {
  window.upcartSubscribeCartLoaded(({ cart }) => {
    const props = document.querySelectorAll('.upcart-public-product-properties__item');
    props.forEach((prop) => {
      const container = prop.closest('.upcart-public-component-product-tile__properties')
      const span = prop.querySelector('span');
      if(span.innerText.includes('Choose your')) {
        span.innerText = span.innerText.replace('Choose your ', '');
        span.style.textTransform = 'capitalize';
        span.style.fontSize = '12px';
      }
      container.style.marginBottom = '8px';
    });
  });
});
